import styled from 'styled-components';
import { ReactNode } from 'react';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';

export type DividerOrientation = 'horizontal' | 'vertical';
export type DividerLabelPosition = 'left' | 'center' | 'right';
export type DividerSpacing = 'none' | 'sm' | 'md' | 'lg' | 'xl';

const spacingMap: Record<DividerSpacing, number> = {
  none: 0,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

export interface DividerProps {
  /** Orientation of the divider */
  orientation?: DividerOrientation;
  /** Label to display in the divider */
  label?: ReactNode;
  /** Position of the label */
  labelPosition?: DividerLabelPosition;
  /** Spacing around the divider */
  spacing?: DividerSpacing;
  /** Whether to use dashed line */
  dashed?: boolean;
  /** Custom color for the line */
  color?: string;
  /** Vertical only: line length in px. Defaults to the text height (1em). */
  length?: number;
  /** Additional class name */
  className?: string;
  /** Test id for testing */
  'data-testid'?: string;
}

const HorizontalDivider = styled.div<{
  $spacing: number;
  $dashed: boolean;
  $color: string;
  $hasLabel: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  margin: ${({ $spacing }) => $spacing}px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ $dashed, $color }) =>
      $dashed
        ? `repeating-linear-gradient(90deg, ${$color} 0, ${$color} 4px, transparent 4px, transparent 8px)`
        : $color};
  }

  ${({ $hasLabel }) =>
    !$hasLabel &&
    `
    &::after {
      display: none;
    }
  `}
`;

const VerticalDivider = styled.div<{
  $spacing: number;
  $dashed: boolean;
  $color: string;
  $length?: number;
}>`
  display: inline-block;
  width: 1px;
  min-height: ${({ $length }) => ($length ? `${$length}px` : '1em')};
  margin: 0 ${({ $spacing }) => $spacing}px;
  vertical-align: middle;
  background: ${({ $dashed, $color }) =>
    $dashed
      ? `repeating-linear-gradient(180deg, ${$color} 0, ${$color} 4px, transparent 4px, transparent 8px)`
      : $color};
`;

const Label = styled.span<{ $position: DividerLabelPosition }>`
  padding: 0 16px;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.body.small.fontSize}px;
  color: ${colors.neutral[600]};
  white-space: nowrap;

  ${({ $position }) =>
    $position === 'left' &&
    `
    padding-left: 0;
  `}

  ${({ $position }) =>
    $position === 'right' &&
    `
    padding-right: 0;
  `}
`;

const LeftLabel = styled(HorizontalDivider)`
  &::before {
    flex: 0;
  }
`;

const RightLabel = styled(HorizontalDivider)`
  &::after {
    flex: 0;
  }
`;

/**
 * Divider component based on Libertify Design System
 *
 * A visual separator for content.
 *
 * @example
 * ```tsx
 * import { Divider } from '@libertify/ui';
 *
 * // Basic horizontal divider
 * <Divider />
 *
 * // With label
 * <Divider label="OR" />
 *
 * // Vertical divider
 * <Divider orientation="vertical" />
 *
 * // Dashed style
 * <Divider dashed />
 * ```
 */
export const Divider = ({
  orientation = 'horizontal',
  label,
  labelPosition = 'center',
  spacing = 'md',
  dashed = false,
  color = colors.neutral[300],
  length,
  className,
  'data-testid': testId,
}: DividerProps) => {
  const spacingValue = spacingMap[spacing];

  if (orientation === 'vertical') {
    return (
      <VerticalDivider
        $spacing={spacingValue}
        $dashed={dashed}
        $color={color}
        $length={length}
        className={className}
        data-testid={testId}
        role="separator"
        aria-orientation="vertical"
      />
    );
  }

  const commonProps = {
    $spacing: spacingValue,
    $dashed: dashed,
    $color: color,
    $hasLabel: !!label,
    className,
    'data-testid': testId,
    role: 'separator' as const,
    'aria-orientation': 'horizontal' as const,
  };

  const labelContent = label && (
    <Label $position={labelPosition}>{label}</Label>
  );

  if (labelPosition === 'left') {
    return <LeftLabel {...commonProps}>{labelContent}</LeftLabel>;
  }

  if (labelPosition === 'right') {
    return <RightLabel {...commonProps}>{labelContent}</RightLabel>;
  }

  return <HorizontalDivider {...commonProps}>{labelContent}</HorizontalDivider>;
};

export default Divider;
